import { useState, useEffect } from 'react';
import { CollapsibleSidebar } from './components/CollapsibleSidebar';
import { ThemeSelector } from './components/ThemeSelector';
import { RegisterPage } from './components/RegisterPage';
import { LoginPage } from './components/LoginPage';
import { ActivationPage } from './components/ActivationPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { ForgotPasswordPage } from './components/ForgotPasswordPage';
import { SetPasswordPage } from './components/SetPasswordPage';
import { Badge } from './components/ui/badge';
import { useAuth } from './src/hooks/useAuth';
import { Toaster } from './components/ui/toaster';
import { dismissAllToasts } from './hooks/use-toast';
import { GlobalConfirmDialogProvider } from './components/ui/confirm-dialog';

// Module Components
import { Dashboard } from './components/modules/Dashboard';
import { 
  AllProjects, 
  SchedulesTasks, 
  DailyLogs, 
  Documents, 
  RFIs, 
  Submittals, 
  Inspections, 
  SafetyIncidents, 
  PunchLists 
} from './components/modules/ProjectManagement';
import { BOQManagement, Estimates, BidsTenders } from './components/modules/PreConstruction';
import { 
  Suppliers, 
  PurchaseOrders, 
  GoodsReceived, 
  Inventory, 
  MaterialRequisitions 
} from './components/modules/ProcurementInventory';
import { 
  Contracts, 
  ChangeOrders, 
  InvoicesAP, 
  PaymentApplicationsAR, 
  Payments, 
  Timesheets, 
  CostCodes 
} from './components/modules/financial';
import { OrganizationProfile, UserManagement, MyProfile } from './components/modules/Settings';
import { BudgetMonitoring } from './components/BudgetMonitoring';
import { ReportingDashboard } from './components/ReportingDashboard';
import { NotificationsPage } from './components/modules/NotificationsPage';
import { NotificationBell } from './components/NotificationBell';
import { ProjectDetailPage } from './components/modules/project_management/ProjectDetailPage';

import { ShieldX } from 'lucide-react';
import { usePermission } from './src/utils/permissions';
import { AppNavContext } from './src/contexts/app-nav.context';
import { useQueryClient } from '@tanstack/react-query';
import { useBillingStatus, BILLING_STATUS_KEY } from './src/hooks/useBilling';
import { TrialBanner } from './components/TrialBanner';
import { TrialExpiredGate } from './components/TrialExpiredGate';
import { PlanSelectionGate } from './components/PlanSelectionGate';
import { BillingCheckout } from './components/BillingCheckout';

type AppView = 'register' | 'login' | 'activate' | 'forgot-password' | 'set-password' | 'dashboard' | 'terms' | 'privacy';

export default function App() {
  const { user, isAuthenticated, logout: authLogout, loading } = useAuth();
  const { canAccessRoute } = usePermission();
  const { data: billingStatus } = useBillingStatus();
  const queryClient = useQueryClient();
  const [showCheckout, setShowCheckout] = useState(false);

  // New users: backend returns 'expired' before any trial or plan is activated.
  // Distinguish them from users whose trial/subscription genuinely expired.
  const isNewUser =
    billingStatus?.status === 'expired' &&
    !billingStatus.is_trial &&
    !billingStatus.subscription_id &&
    !billingStatus.trial_ends_at;
  const [currentView, setCurrentView] = useState<AppView>('login');
  const [activeModule, setActiveModule] = useState<string>('/dashboard');
  const [routeFilters, setRouteFilters] = useState<Record<string, string>>({});
  const [setPasswordToken, setSetPasswordToken] = useState<string>('');
  const [setPasswordMode, setSetPasswordMode] = useState<'set' | 'reset'>('set');

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      // Don't handle navigation while auth is loading
      if (loading) return;

      const currentPath = window.location.pathname;
      const publicRoutes = ['/', '/register', '/login', '/activate', '/verify-email', '/reset-password', '/terms', '/privacy'];

      if (isAuthenticated) {
        if (publicRoutes.includes(currentPath) && currentPath !== '/terms' && currentPath !== '/privacy') {
          setCurrentView('dashboard');
          setActiveModule('/dashboard');
        } else if (currentPath === '/terms') {
          setCurrentView('terms');
        } else if (currentPath === '/privacy') {
          setCurrentView('privacy');
        } else {
          setCurrentView('dashboard');
          setActiveModule(currentPath);
        }
      } else {
        if (currentPath === '/register') {
          setCurrentView('register');
        } else if (currentPath === '/terms') {
          setCurrentView('terms');
        } else if (currentPath === '/privacy') {
          setCurrentView('privacy');
        } else if (currentPath === '/activate' || currentPath === '/verify-email') {
          setCurrentView('activate');
        } else if (currentPath === '/reset-password') {
          const token = new URLSearchParams(window.location.search).get('token') ?? '';
          setSetPasswordToken(token);
          setSetPasswordMode('reset');
          setCurrentView('set-password');
        } else {
          // Default unauthenticated view is login; protected routes redirect here too
          if (!publicRoutes.includes(currentPath)) {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
            window.history.replaceState({}, '', '/login');
          }
          setCurrentView('login');
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated, loading]);

  // Handle authentication state changes and URL-based routing
  useEffect(() => {
    // Don't run routing logic while auth is still loading
    if (loading) return;

    // Check for activation routes (user-initiated or email link)
    const activationPaths = ['/activate', '/verify-email'];
    if (activationPaths.includes(window.location.pathname)) {
      setCurrentView('activate');
      return;
    }

    // Password-reset link from email: /reset-password?token=TOKEN
    if (window.location.pathname === '/reset-password') {
      const token = new URLSearchParams(window.location.search).get('token') ?? '';
      setSetPasswordToken(token);
      setSetPasswordMode('reset');
      setCurrentView('set-password');
      return;
    }

    const publicRoutes = ['/', '/register', '/login', '/terms', '/privacy'];
    const currentPath = window.location.pathname;

    if (currentPath === '/terms') {
      setCurrentView('terms');
      return;
    }
    if (currentPath === '/privacy') {
      setCurrentView('privacy');
      return;
    }

    if (isAuthenticated) {
      if (publicRoutes.includes(currentPath)) {
        setCurrentView('dashboard');
        setActiveModule('/dashboard');
        window.history.pushState({}, '', '/dashboard');
      } else {
        setCurrentView('dashboard');
        setActiveModule(currentPath);
      }
    } else {
      if (!publicRoutes.includes(currentPath)) {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
        setCurrentView('login');
        window.history.pushState({}, '', '/login');
      } else if (currentPath === '/register') {
        setCurrentView('register');
      } else {
        setCurrentView('login');
      }
    }
  }, [isAuthenticated, loading]);

  const handleGetStarted = () => {
    setCurrentView('register');
    window.history.pushState({}, '', '/register');
  };

  const handleLogin = () => {
    setCurrentView('login');
    window.history.pushState({}, '', '/login');
  };

  const handleBackToLanding = () => {
    window.location.href = 'https://optibuild.pro';
  };

  const handleRegistrationComplete = () => {
    // View will be set by useEffect when authentication state changes
  };

  const handleLoginComplete = () => {
    dismissAllToasts();
    // Check for redirect URL stored before login
    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
    if (redirectUrl) {
      sessionStorage.removeItem('redirectAfterLogin');
      setActiveModule(redirectUrl);
      window.history.pushState({}, '', redirectUrl);
    }
    // View will be set by useEffect when authentication state changes
  };

  const handleActivate = () => {
    window.history.pushState({}, '', '/activate');
    setCurrentView('activate');
  };

  const handleBackFromLegal = () => {
    window.history.pushState({}, '', '/register');
    setCurrentView('register');
  };

  const handleActivationComplete = () => {
    dismissAllToasts();
    window.history.pushState({}, '', '/login');
    setCurrentView('login');
  };

  const handleForgotPassword = () => {
    window.history.pushState({}, '', '/forgot-password');
    setCurrentView('forgot-password');
  };

  /** Called by ActivationPage when backend returns a set_password_token after verification */
  const handleSetPassword = (token: string) => {
    setSetPasswordToken(token);
    setSetPasswordMode('set');
    window.history.pushState({}, '', '/set-password');
    setCurrentView('set-password');
  };

  /**
   * Called by SetPasswordPage on success.
   * mode='set'   → auth tokens already stored; auth state effect will navigate to dashboard.
   * mode='reset' → no tokens; go to login with a clean URL.
   */
  const handleSetPasswordComplete = () => {
    setSetPasswordToken('');
    if (setPasswordMode === 'reset') {
      window.history.pushState({}, '', '/login');
      setCurrentView('login');
    }
    // mode='set': tokens already stored by setPassword(); auth useEffect navigates to dashboard
  };

  const handleLogout = async () => {
    dismissAllToasts();
    await authLogout();
    setCurrentView('login');
    setActiveModule('/dashboard');
    window.history.pushState({}, '', '/login');
    sessionStorage.removeItem('redirectAfterLogin');
  };

  const handleNavigate = (path: string) => {
    setActiveModule(path);
    setRouteFilters({});
    window.history.pushState({}, '', path);
  };

  const appNavigate = (path: string, filters?: Record<string, string>) => {
    setActiveModule(path);
    setRouteFilters(filters ?? {});
    window.history.pushState({}, '', path);
  };

  const renderAccessDenied = () => (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
      <ShieldX className="h-16 w-16 text-muted-foreground/50" />
      <div>
        <h2 className="text-xl font-semibold mb-1">Access Restricted</h2>
        <p className="text-muted-foreground text-sm">
          You don't have permission to view this module.<br />
          Contact your administrator if you need access.
        </p>
      </div>
    </div>
  );

  const renderActiveModule = () => {
    if (!canAccessRoute(activeModule)) return renderAccessDenied();
    switch (activeModule) {
      // Dashboard
      case '/dashboard':
        return <Dashboard />;

      // Project Management
      case '/projects':
        return <AllProjects />;
      case '/projects/detail':
        return <ProjectDetailPage projectId={routeFilters.projectId ?? ''} />;
      case '/schedules':
        return <SchedulesTasks />;
      case '/daily-logs':
        return <DailyLogs />;
      case '/documents':
        return <Documents />;
      case '/rfis':
        return <RFIs />;
      case '/submittals':
        return <Submittals />;
      case '/inspections':
        return <Inspections />;
      case '/safety-incidents':
        return <SafetyIncidents />;
      case '/punch-lists':
        return <PunchLists />;

      // Pre-Construction & Planning
      case '/boq':
        return <BOQManagement />;
      case '/estimates':
        return <Estimates />;
      case '/bids':
        return <BidsTenders />;

      // Procurement & Inventory
      case '/suppliers':
        return <Suppliers />;
      case '/purchase-orders':
        return <PurchaseOrders />;
      case '/goods-received':
        return <GoodsReceived />;
      case '/inventory':
        return <Inventory />;
      case '/material-requisitions':
        return <MaterialRequisitions />;

      // Financials & Contracts
      case '/budget-cost':
        return <BudgetMonitoring />;
      case '/contracts':
        return <Contracts />;
      case '/change-orders':
        return <ChangeOrders />;
      case '/invoices-ap':
        return <InvoicesAP />;
      case '/payment-applications-ar':
        return <PaymentApplicationsAR />;
      case '/payments':
        return <Payments />;
      case '/timesheets':
        return <Timesheets />;
      case '/cost-codes':
        return <CostCodes />;

      // Reports & Analytics
      case '/reports':
        return <ReportingDashboard />;

      // Notifications
      case '/notifications':
        return <NotificationsPage />;

      // Settings
      case '/settings/profile':
        return <MyProfile />;
      case '/settings/organization':
        return <OrganizationProfile />;
      case '/settings/users':
        return <UserManagement />;

      default:
        return <Dashboard />;
    }
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const getPageTitle = () => {
    const titles: { [key: string]: string } = {
      '/dashboard': 'Dashboard',
      '/projects': 'All Projects',
      '/schedules': 'Schedules & Tasks',
      '/daily-logs': 'Daily Logs',
      '/documents': 'Documents',
      '/rfis': 'RFIs',
      '/submittals': 'Submittals',
      '/inspections': 'Inspections & QA/QC',
      '/safety-incidents': 'Safety Incidents',
      '/punch-lists': 'Punch Lists',
      '/boq': 'BOQ Management',
      '/estimates': 'Estimates',
      '/bids': 'Bids & Tenders',
      '/suppliers': 'Suppliers',
      '/purchase-orders': 'Purchase Orders',
      '/goods-received': 'Goods Received',
      '/inventory': 'Inventory',
      '/material-requisitions': 'Material Requisitions',
      '/budget-cost': 'Budget & Cost Control',
      '/contracts': 'Contracts',
      '/change-orders': 'Change Orders',
      '/invoices-ap': 'Invoices (AP)',
      '/payment-applications-ar': 'Payment Applications (AR)',
      '/payments': 'Payments',
      '/timesheets': 'Timesheets',
      '/cost-codes': 'Cost Codes',
      '/reports': 'Reports & Analytics',
      '/settings/profile': 'My Profile',
      '/settings/organization': 'Organization Profile',
      '/settings/users': 'User Management',
      '/notifications': 'Notifications',
    };
    return titles[activeModule] || 'Dashboard';
  };

  // Render terms of service page
  if (currentView === 'terms') {
    return <TermsOfServicePage onBack={handleBackFromLegal} />;
  }

  // Render privacy policy page
  if (currentView === 'privacy') {
    return <PrivacyPolicyPage onBack={handleBackFromLegal} />;
  }

  // Render registration page
  if (currentView === 'register') {
    return (
      <RegisterPage
        onBackToLanding={handleBackToLanding}
        onLogin={handleLogin}
        onRegistrationComplete={handleRegistrationComplete}
        onActivate={handleActivate}
      />
    );
  }

  // Render login page
  if (currentView === 'login') {
    return (
      <LoginPage
        onBackToLanding={handleBackToLanding}
        onRegister={handleGetStarted}
        onLoginComplete={handleLoginComplete}
        onForgotPassword={handleForgotPassword}
      />
    );
  }

  // Render activation page
  if (currentView === 'activate') {
    return (
      <ActivationPage
        onBackToLogin={() => setCurrentView('login')}
        onActivationComplete={handleActivationComplete}
        onSetPassword={handleSetPassword}
      />
    );
  }

  // Render forgot-password page
  if (currentView === 'forgot-password') {
    return (
      <ForgotPasswordPage
        onBackToLogin={handleLogin}
      />
    );
  }

  // Render set-password page (new user) or reset-password page (forgot password flow)
  if (currentView === 'set-password') {
    return (
      <SetPasswordPage
        mode={setPasswordMode}
        token={setPasswordToken}
        onSuccess={handleSetPasswordComplete}
        onBackToLogin={handleLogin}
      />
    );
  }

  // Render dashboard (authenticated view)
  return (
    <>
      <div className="flex h-screen w-full">
        {/* Sidebar */}
        <CollapsibleSidebar
          activeModule={activeModule}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          <header className="border-b border-border p-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-2">
              <ThemeSelector />
              <NotificationBell
                onViewAll={() => handleNavigate('/notifications')}
                onNavigate={handleNavigate}
              />
              {billingStatus?.is_trial && billingStatus.status !== 'active' && (
                <Badge variant="secondary">
                  {billingStatus.trial_days_remaining != null
                    ? `${billingStatus.trial_days_remaining}d trial`
                    : 'Trial'}
                </Badge>
              )}
            </div>
          </header>

          {billingStatus && billingStatus.status !== 'active' && activeModule !== '/dashboard' && (
            <TrialBanner
              status={billingStatus}
              onUpgradeClick={() => setShowCheckout(true)}
            />
          )}

          <main className="flex-1 overflow-auto p-6 relative">
            {billingStatus?.status === 'expired' && isNewUser && (
              <PlanSelectionGate
                onTrialStarted={() =>
                  queryClient.invalidateQueries({ queryKey: BILLING_STATUS_KEY })
                }
              />
            )}
            {billingStatus?.status === 'expired' && !isNewUser && (
              <TrialExpiredGate onUpgradeClick={() => setShowCheckout(true)} />
            )}
            <AppNavContext.Provider value={{ navigate: appNavigate, routeFilters }}>
              {renderActiveModule()}
            </AppNavContext.Provider>
          </main>

          {/* Footer */}
          <footer className="border-t border-border bg-background py-3 px-6">
            <div className="flex items-center justify-center text-xs text-muted-foreground">
              <p>
                © {new Date().getFullYear()} OptiBuild. All rights reserved. |{' '}
                <a
                  href="https://optibuild.pro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Visit us at optibuild.pro
                </a>
              </p>
            </div>
          </footer>
        </div>
      </div>
      <BillingCheckout open={showCheckout} onClose={() => setShowCheckout(false)} />
      <Toaster />
      <GlobalConfirmDialogProvider />
    </>
  );
}